/**
 * Utility functions for determining appraisal workflow permissions
 * Based on user role, record status, and current approval level
 */

/**
 * Get appraisal workflow permissions for a user
 * @param {Object} params
 * @param {string} params.role - User role (supervisor, operations_manager, hr)
 * @param {Object} params.record - Appraisal progress record
 * @param {string} params.currentUserId - Current logged-in user ID
 * @param {string} [params.currentEmployeeId] - Current user's employee id (em_employees.id)
 * @returns {Object} Permission flags
 */
export function getAppraisalPermissions({ role, record, currentUserId, currentEmployeeId }) {
  const empty = {
    canEditDraft: false,
    canSubmit: false,
    canApprove: false,
    canReject: false,
    canSendForRTM: false,
    canView: true,
  };

  if (!record) {
    return empty;
  }

  const normalizeStatus = (status) => {
    const normalized = String(status || "pending").toLowerCase();
    if (normalized === "sent_rtm") return "sent_to_rtm";
    return normalized;
  };

  const statusText = normalizeStatus(record.status || record.appraisal_status || "pending");
  const signOff =
    record.sign_off === 1 ||
    record.sign_off === true ||
    record.sign_off === "1" ||
    record.signOff === 1 ||
    record.signOff === true ||
    record.signOff === "1";

  const isFinal = [
    "level_one_approved",
    "level_one_rejected",
    "approved",
    "rejected",
    "auto_approved",
    "completed",
  ].includes(statusText);

  const approvalLevels = Number(record.approval_levels ?? record.approvalLevels ?? 0);
  if (approvalLevels === 1) {
    const isAppraiser = isCurrentUserAppraiser(record, currentUserId, currentEmployeeId);
    const canAct =
      isAppraiser &&
      (statusText === "pending" ||
        statusText === "draft" ||
        statusText === "returned_to_supervisor");

    return {
      canEditDraft: canAct,
      canSubmit: false,
      canApprove: canAct && signOff && !isFinal,
      canReject: canAct && signOff && !isFinal,
      canSendForRTM: false,
      canView: true,
    };
  }

  // Prefer session role, but fall back to what backend includes with the record.
  const effectiveRole =
    role ||
    record.current_user_role ||
    record.currentUserRole ||
    record.user_role ||
    record.userRole ||
    null;

  if (!effectiveRole) {
    return empty;
  }

  const normalizeRoleKey = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/-/g, "_");

  const normalizedUserRole = normalizeRoleKey(effectiveRole);
  const isSupervisor = normalizedUserRole === "supervisor";

  const compactRole = normalizedUserRole.replace(/[^a-z0-9]/g, "");
  const isManager = compactRole === "manager" || compactRole === "operationsmanager";
  const inManagerRtmQueue = statusText === "sent_to_rtm" || statusText === "auto_sent_rtm";
  const secondApproverId = String(
    record.second_approver_id ?? record.secondApproverId ?? ""
  ).trim();

  let canApprove = false;
  let canReject = false;

  // Mirror AppraisalProgressPolicy::approve for two-level / legacy PAFs:
  // Manager role, RTM queue, and snapshotted L2 when second_approver_id is set.
  if (!isFinal && isManager && inManagerRtmQueue) {
    if (secondApproverId) {
      canApprove =
        !!currentEmployeeId && String(currentEmployeeId) === secondApproverId;
    } else {
      canApprove = true;
    }
    canReject = canApprove;
  }

  // Hard stop: supervisors never see list Approve/Reject (even if config or fallbacks misfire).
  if (isSupervisor) {
    canApprove = false;
    canReject = false;
  }

  return applyBackendApproveRejectFlags({
    // Default active status is pending (keep draft for backward compatibility).
    // returned_to_supervisor: supervisor may revise after HR/OM return (backend sets can_edit via appraisal_status_level_permissions).
    canEditDraft:
      isSupervisor &&
      (statusText === "pending" ||
        statusText === "draft" ||
        statusText === "returned_to_supervisor"),
    // Monthly / progress list: Submit is not exposed in the grid (use the form / save flow instead).
    canSubmit: false,

    // Level-based actions
    canApprove,
    canReject,

    // Supervisor can send for RTM after sign-off until it is sent/approved/rejected
    canSendForRTM:
      isSupervisor &&
      signOff &&
      !["sent_to_rtm", "auto_sent_rtm", "level_one_approved", "level_one_rejected", "completed", "approved"].includes(
        statusText
      ),

    canView: true,
  }, record);
}

function hasExplicitFlag(value) {
  return value !== undefined && value !== null && value !== "";
}

function flagEnabled(value) {
  return value === 1 || value === true || value === "1";
}

/**
 * Prefer backend workflow flags (AppraisalProgressPolicy via can_approve/can_reject)
 * when the API included them on the record.
 */
function applyBackendApproveRejectFlags(permissions, record) {
  if (!record) {
    return permissions;
  }

  const next = { ...permissions };
  const apiCanApprove = record.can_approve ?? record.canApprove;
  const apiCanReject = record.can_reject ?? record.canReject;

  if (hasExplicitFlag(apiCanApprove)) {
    next.canApprove = flagEnabled(apiCanApprove);
  }
  if (hasExplicitFlag(apiCanReject)) {
    next.canReject = flagEnabled(apiCanReject);
  } else if (hasExplicitFlag(apiCanApprove)) {
    next.canReject = next.canApprove;
  }

  return next;
}

/**
 * Coerce API/session role values to a display key string (same shapes as TopNav).
 * @param {unknown} value
 * @returns {string|null}
 */
function coerceSessionRoleValue(value) {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    const t = value.trim();
    return t || null;
  }
  if (typeof value === "object") {
    const label = value.name ?? value.role_name ?? value.title;
    if (label != null && String(label).trim()) return String(label).trim();
    return null;
  }
  return String(value).trim() || null;
}

/**
 * Get user role from session (string key for workflows / comparisons).
 * Handles `role` as string or `{ name, role_name }` (common API shapes).
 */
export function getUserRole(session) {
  if (!session || !session.user || typeof session.user !== "object") {
    return null;
  }

  const user = session.user;

  let role =
    coerceSessionRoleValue(user.role) ??
    coerceSessionRoleValue(user.user_role) ??
    coerceSessionRoleValue(user.role_name);

  if (!role && Array.isArray(user.roles) && user.roles.length > 0) {
    role = coerceSessionRoleValue(user.roles[0]);
  }

  if (!role) {
    role = coerceSessionRoleValue(user.designation?.role);
  }

  return role || null;
}

/**
 * Get current user ID from session
 * @param {Object} session - Session object from Redux
 * @returns {string} User ID
 */
export function getCurrentUserId(session) {
  if (!session || !session.user) {
    return null;
  }

  return session.user.id || 
         session.user.user_id || 
         session.user.uid ||
         null;
}

/**
 * Employee id of the logged-in user (em_employees.id). Does not fall back to user id.
 */
export function getCurrentEmployeeId(session) {
  if (!session?.user) return null;
  return (
    session.user.employee_id ??
    session.user.employeeId ??
    session.user.employee?.id ??
    null
  );
}

function isCurrentUserAppraiser(record, currentUserId, currentEmployeeId) {
  if (currentEmployeeId && record.appraiser_id && String(currentEmployeeId) === String(record.appraiser_id)) {
    return true;
  }
  if (currentEmployeeId && record.latest_reporting_to && String(currentEmployeeId) === String(record.latest_reporting_to)) {
    return true;
  }
  const appraiserUserId =
    record.appraiser?.user_id ??
    record.appraiser?.userId ??
    record.appraiser_user_id;
  if (currentUserId && appraiserUserId && String(currentUserId) === String(appraiserUserId)) {
    return true;
  }
  return false;
}

/**
 * Get workflow status badge color
 * @param {string} status - Appraisal status
 * @param {string} level - Current approval level
 * @returns {string} Bootstrap badge class
 */
export function getStatusBadgeClass(status, level) {
  const normalizedStatusRaw = (status || '').toLowerCase();
  const normalizedStatus = normalizedStatusRaw === "sent_rtm" ? "sent_to_rtm" : normalizedStatusRaw;
  const normalizedLevel = (level || '').toLowerCase();

  if (normalizedStatus === 'pending' || normalizedStatus === 'draft') {
    return 'bg-secondary';
  }
  if (normalizedStatus === 'sent_to_rtm') {
    return 'bg-primary';
  }
  if (normalizedStatus === 'level_one_approved' || normalizedStatus === 'approved' || normalizedLevel === 'hr') {
    return 'bg-success';
  }
  if (normalizedStatus === 'level_one_rejected' || normalizedStatus === 'rejected') {
    return 'bg-danger';
  }
  if (normalizedStatus === 'signed_off' || normalizedStatus === 'completed') {
    return 'bg-info';
  }

  return 'bg-secondary';
}

/**
 * Get workflow step status
 * @param {Object} record - Appraisal progress record
 * @param {string} step - Step name (supervisor, operations_manager, hr)
 * @returns {string} Status (pending, in_progress, completed, rejected)
 */
export function getWorkflowStepStatus(record, step) {
  const statusRaw = (record.status || '').toLowerCase();
  const status = statusRaw === "sent_rtm" ? "sent_to_rtm" : statusRaw;
  const level = (record.current_approval_level || '').toLowerCase();

  if (step === 'supervisor') {
    if (status === 'pending' || status === 'draft') return 'in_progress';
    if (status === 'sent_to_rtm' || status === 'level_one_approved' || status === 'level_one_rejected') return 'completed';
    return 'pending';
  }

  if (step === 'operations_manager') {
    // Removed from active flow; keep backward-safe fallback
    if (status === 'level_one_approved' || status === 'approved') return 'completed';
    if (status === 'level_one_rejected' || status === 'rejected') return 'rejected';
    return 'pending';
  }

  if (step === 'hr') {
    // Removed from active flow; keep backward-safe fallback
    if (status === 'level_one_approved' || status === 'approved' || status === 'completed') return 'completed';
    if (status === 'level_one_rejected' || status === 'rejected') return 'rejected';
    return 'pending';
  }

  return 'pending';
}
